import { Spinner } from 'react-bootstrap';
import ReactEChartsCore from 'echarts-for-react/lib/core';
import * as echarts from 'echarts/core';
import type { EChartsCoreOption } from 'echarts/core';
import { TooltipComponent, GridComponent, LegendComponent, DataZoomComponent } from 'echarts/components';
import { BarChart, LineChart } from 'echarts/charts';
import { CanvasRenderer } from 'echarts/renderers';

import { CHART_HEIGHT, EMPTY_MESSAGES } from '../constants';

echarts.use([TooltipComponent, GridComponent, LegendComponent, DataZoomComponent, BarChart, LineChart, CanvasRenderer]);

export type ChartPanelVariant = 'default' | 'risk' | 'alert';

function EmptyState({ message, height = CHART_HEIGHT }: { message: string; height?: number }) {
  return (
    <div
      className="d-flex align-items-center justify-content-center w-100 realtime-monitoring-tab__empty-state"
      style={{ height, padding: '0 20px', textAlign: 'center' }}
      role="status"
    >
      {message}
    </div>
  );
}

function panelClassName(modern: boolean | undefined, panelVariant: ChartPanelVariant): string {
  if (!modern) return 'realtime-monitoring-tab__chart-panel';
  return [
    'realtime-monitoring-tab__chart-panel',
    'realtime-monitoring-tab__chart-panel--modern',
    panelVariant === 'risk' ? 'realtime-monitoring-tab__chart-panel--risk' : '',
    panelVariant === 'alert' ? 'realtime-monitoring-tab__chart-panel--alert' : ''
  ]
    .filter(Boolean)
    .join(' ');
}

function chartTitleClassName(panelVariant?: ChartPanelVariant): string {
  return [
    'realtime-monitoring-tab__chart-title',
    panelVariant === 'risk' ? 'realtime-monitoring-tab__chart-title--risk' : '',
    panelVariant === 'alert' ? 'realtime-monitoring-tab__chart-title--alert' : ''
  ]
    .filter(Boolean)
    .join(' ');
}

type MonitoringChartProps = {
  option: EChartsCoreOption | null;
  height?: number;
  loading?: boolean;
  modern?: boolean;
  panelVariant?: ChartPanelVariant;
};

export function MonitoringChart({
  option,
  height = CHART_HEIGHT,
  loading,
  modern,
  panelVariant = 'default'
}: MonitoringChartProps) {
  return (
    <div className={panelClassName(modern, panelVariant)} style={{ minHeight: height + 8 }}>
      {loading ? (
        <div className="d-flex align-items-center justify-content-center" style={{ height }}>
          <Spinner animation="border" size="sm" />
        </div>
      ) : option ? (
        <ReactEChartsCore echarts={echarts} option={option} style={{ height, width: '100%' }} notMerge lazyUpdate />
      ) : (
        <EmptyState message={EMPTY_MESSAGES.chart} height={height} />
      )}
    </div>
  );
}

type ChartBlockProps = MonitoringChartProps & {
  title: string;
  fullWidth?: boolean;
  chartHeight?: number;
};

export function ChartBlock({ title, fullWidth, panelVariant, chartHeight, height, ...chartProps }: ChartBlockProps) {
  return (
    <div className={fullWidth ? 'realtime-monitoring-tab__chart-block--full' : 'realtime-monitoring-tab__chart-block'}>
      <h3 className={chartTitleClassName(panelVariant)}>{title}</h3>
      <MonitoringChart panelVariant={panelVariant} height={chartHeight ?? height} {...chartProps} />
    </div>
  );
}
